// @flow

import { all, put, takeEvery, call, select, takeLatest } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import { push } from "connected-react-router";
import { route } from "@routes/helper";
import config from "@config";
import actionCreators from "@modules/service/actionCreator";
import type { AbsenceReason } from "@modules/service/reducer/absence-reasons/types";
import type { HierarchyAssignment, ApprovableContainer } from "@modules/service/types";
import {
  AddNewAbsenceReason,
  GetAbsenceReason,
  UpdateAbsenceReason,
  GetAbsenceReasonsList,
  ExportLiveAbsenceReasons,
  GetAbsenceReasonSchema,
} from "@modules/service/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { selectAbsenceReasonsPage } from "@modules/service/selectors/absence-reasons";
import { downloadCsvFile } from "@sagas/utils";
import { getSchema } from "@utils/schema";
import { transformError } from "@transformers/response";

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type ItemResponse<T> = $Exact<T>;

type AbsenceReasonsListRequest = $Exact<{
  clientId: string,
  absenceReasonTypeId: number,
  isActive: boolean,
  page: number | null,
}>;

type AddAbsenceReasonsRequest = $Exact<{
  clientId: string,
  draft: AbsenceReason,
  assignments: Array<HierarchyAssignment>,
  isActive: true | false | null,
  absenceReasonTypeId: 1 | 2 | null,
}>;

export type AbsenceReasonRequest = $Exact<{
  ...AbsenceReasonsListRequest,
  stableId: string,
}>;

type AbsenceReasonUpdateRequest = $Exact<{
  ...AbsenceReasonRequest,
  draft: AbsenceReason,
}>;

export function* onGetAbsenceReasonsList({
  payload,
}: Action<AbsenceReasonsListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<AbsenceReason>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const currentPage = yield select(selectAbsenceReasonsPage);
    const page = (payload && payload.page) || currentPage;

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason`,
      {
        params: {
          absenceReasonTypeId: payload.absenceReasonTypeId,
          isActive: payload.isActive,
          page,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetAbsenceReasonsListSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAbsenceReasonsListError(e.response));
  }
}

export function* onAddNewAbsenceReason({
  payload,
}: Action<AddAbsenceReasonsRequest>): Generator<*, void, { data: AbsenceReason }> {
  try {
    if (!payload || !payload.clientId || !payload.draft) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason`,
      payload.draft,
    );

    yield all([
      put(actionCreators.GetAbsenceReasonActiveSuccess(response.data)),
      put(actionCreators.ShowAddAbsenceReasonModal(false)),
      put(actionCreators.ResetAbsenceReason()),
      put(
        actionCreators.GetAbsenceReasonsList({
          clientId: payload.clientId,
          isActive: payload.isActive,
          absenceReasonTypeId: payload.absenceReasonTypeId,
        }),
      ),
      put(sendSuccessMessage("ABSENCE_REASON_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddNewAbsenceReasonError(e.response));
  }
}

export function* onGetAbsenceReason({
  payload,
}: Action<AbsenceReasonRequest>): Generator<*, void, { data: AbsenceReason }> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/${payload.stableId}`,
    );

    yield put(actionCreators.GetAbsenceReasonSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAbsenceReasonError(e.response));
  }
}

export function* onUpdateAbsenceReason({
  payload,
}: Action<AbsenceReasonUpdateRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<AbsenceReason>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.stableId || !payload.draft) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const draft = {
      displayName: payload.draft.displayName,
      isActive: payload.draft.isActive,
      name: payload.draft.name,
      nurseTransferEligibility: payload.draft.nurseTransferEligibility,
      nurseTransferEligibilityAbsenceDuration:
        payload.draft.nurseTransferEligibilityAbsenceDuration,
      parentReason: payload.draft.parentReason,
      reasonType: payload.draft.reasonType,
    };

    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/${payload.stableId}`,
      draft,
    );

    yield all([
      put(actionCreators.UpdateAbsenceReasonSuccess(response.data)),
      put(actionCreators.ShowAbsenceReasonEditModal(false)),
      put(actionCreators.ResetAbsenceReason()),
      put(
        actionCreators.GetAbsenceReasonsList({
          clientId: payload.clientId,
          isActive: payload.isActive,
          absenceReasonTypeId: payload.absenceReasonTypeId,
        }),
      ),
      put(sendSuccessMessage("DRAFT_UPDATED")),
      put(push(route("ADMINISTRATION_CLIENT_FEATURES_PATH", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateAbsenceReasonError(e.response));
  }
}

function* onExportLiveAbsenceReasons({
  payload,
}: Action<{ clientId: number }>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const response = yield call(
      http.get,
      `api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/client-absence-reason/reason/export`,
    );
    downloadCsvFile(response.data, `Absence_Reasons-${payload.clientId}.csv`);
    yield put(actionCreators.ExportLiveAbsenceReasonsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportLiveAbsenceReasonsError(e.response));
  }
}

export function* onGetAbsenceReasonSchema(): Generator<*, void, Object> {
  try {
    const response = yield call(http.get, `api/v1/data/parent-absence-reason`); // retrieve parent reasons
    const schema = yield getSchema("ClientAbsenceReasonRevisionDTO"); // retrieve the schema
    let newSchema = JSON.parse(JSON.stringify(schema)); // copy of the schema
    let medicalReasons = [];
    let nonMedicalReasons = [];

    response.data.content.forEach(el => {
      // prepare to match a dropdown selector data format
      const res = {
        title: el.name,
        const: el.id,
      };

      // medical id = 1
      // non-medical id = 2
      if (el.typeId === 1) {
        medicalReasons.push(res);
      } else {
        nonMedicalReasons.push(res);
      }
    });
    // replace parent reasons
    newSchema.properties.parentReason.properties.id.oneOf = [medicalReasons[0]];
    newSchema.then.properties.parentReason.properties.id.oneOf = medicalReasons;
    newSchema.else.properties.parentReason.properties.id.oneOf = nonMedicalReasons;

    yield put(actionCreators.GetAbsenceReasonSchemaSuccess(newSchema));
  } catch (e) {
    yield put(actionCreators.GetAbsenceReasonSchemaError(transformError(e)));
  }
}

export function* AbsenceReasonsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetAbsenceReasonsList, onGetAbsenceReasonsList),
    takeLatest(AddNewAbsenceReason, onAddNewAbsenceReason),
    takeEvery(GetAbsenceReason, onGetAbsenceReason),
    takeEvery(UpdateAbsenceReason, onUpdateAbsenceReason),
    takeEvery(ExportLiveAbsenceReasons, onExportLiveAbsenceReasons),
    takeEvery(GetAbsenceReasonSchema, onGetAbsenceReasonSchema),
  ]);
}

export default AbsenceReasonsSagas;
